import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Empty } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { DollarSign, Loader2, Plus, FileText, Repeat, Clock, Trash2 } from "lucide-react";
import { toast } from "sonner";

const INV_COLOR = { paid: "#10B981", open: "#F5A623", partially_paid: "#002EA6", void: "#71717A" };
const INV_STATUSES = [
  { v: "open", l: "Open" },
  { v: "partially_paid", l: "Partially paid" },
  { v: "paid", l: "Paid" },
  { v: "void", l: "Void" },
];

export default function AdminBilling() {
  const [ov, setOv] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [subs, setSubs] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ organization_id: "", invoice_type: "engaged_search", description: "", amount: "", allow_installments: false, installments: 3 });

  const load = () => {
    api.get("/admin/overview").then(({ data }) => setOv(data));
    api.get("/invoices").then(({ data }) => setInvoices(data));
    api.get("/subscriptions").then(({ data }) => setSubs(data));
  };
  useEffect(() => { load(); api.get("/organizations").then(({ data }) => setOrgs(data)); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!f.organization_id) return toast.error("Select an organization");
    try {
      await api.post("/invoices", { organization_id: f.organization_id, invoice_type: f.invoice_type, description: f.description, amount: parseFloat(f.amount), currency: "usd", status: "open", allow_installments: f.allow_installments, installments: Math.max(1, parseInt(f.installments) || 1) });
      toast.success("Invoice created");
      setOpen(false); setF({ organization_id: "", invoice_type: "engaged_search", description: "", amount: "", allow_installments: false, installments: 3 }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const changeStatus = async (id, status) => {
    try { await api.patch(`/invoices/${id}`, { status }); toast.success(`Invoice marked ${status.replace(/_/g, " ")}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const removeInvoice = async (i) => {
    if (!window.confirm(`Delete this ${(i.invoice_type || "").replace(/_/g, " ")} invoice for ${i.organization_name} ($${i.amount})? This cannot be undone.`)) return;
    try { await api.delete(`/invoices/${i.id}`); toast.success("Invoice deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleInstallments = async (i) => {
    await api.patch(`/invoices/${i.id}`, { allow_installments: !i.allow_installments, installments: i.installments > 1 ? i.installments : 3 });
    toast.success(i.allow_installments ? "Installments disabled" : "Installments enabled for client");
    load();
  };

  if (!ov) return <div className="h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const cards = [
    { label: "Revenue (paid)", value: `$${ov.revenue.toLocaleString()}`, icon: DollarSign },
    { label: "Open Invoices", value: ov.open_invoices, icon: Clock },
    { label: "Active Subs", value: ov.active_subscriptions, icon: Repeat },
    { label: "Published Jobs", value: ov.published_jobs, icon: FileText },
  ];

  return (
    <div>
      <PageHeader overline="Revenue & operations" title="Billing">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-invoice-btn" className="rounded-none bg-primary hover:bg-[#09090B]"><Plus className="h-4 w-4 mr-1" /> Invoice</Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-black/10 rounded-none">
            <DialogHeader><DialogTitle className="font-heading">Create Invoice</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="space-y-2">
                <Label>Organization</Label>
                <Select value={f.organization_id} onValueChange={(v) => setF({ ...f, organization_id: v })}>
                  <SelectTrigger data-testid="inv-org" className="bg-[#F4F4F5] border-black/10 rounded-none"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white border-black/10">{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={f.invoice_type} onValueChange={(v) => setF({ ...f, invoice_type: v })}>
                    <SelectTrigger className="bg-[#F4F4F5] border-black/10 rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-black/10">
                      {["engaged_search", "placement_fee", "job_posting", "subscription"].map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Amount (USD)</Label><Input data-testid="inv-amount" type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} required className="bg-[#F4F4F5] border-black/10 rounded-none" /></div>
              </div>
              <div className="space-y-2">
                <Label>What is this invoice for?</Label>
                <Textarea data-testid="inv-description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
                  placeholder="e.g. Engaged search — VP Engineering. First of three milestone payments covering search kickoff and initial slate delivery."
                  className="bg-[#F4F4F5] border-black/10 rounded-none min-h-[80px]" />
                <p className="text-xs text-muted-foreground">Shown to the client on their invoice.</p>
              </div>
              <div className="border border-black/10 p-3 space-y-3">
                <label className="flex items-center justify-between text-sm cursor-pointer">
                  <span>Allow client to pay in installments</span>
                  <Switch checked={f.allow_installments} onCheckedChange={(v) => setF({ ...f, allow_installments: v })} data-testid="inv-installments-toggle" />
                </label>
                {f.allow_installments && (
                  <div className="space-y-2"><Label>Number of installments</Label><Input type="number" min="2" value={f.installments} onChange={(e) => setF({ ...f, installments: e.target.value })} className="bg-[#F4F4F5] border-black/10 rounded-none" /></div>
                )}
              </div>
              <DialogFooter><Button type="submit" data-testid="inv-save" className="rounded-none bg-primary hover:bg-[#09090B]">Create</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="p-8 space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10">
          {cards.map((c) => (
            <div key={c.label} className="bg-white p-6">
              <c.icon className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
              <p className="font-heading font-black text-4xl mt-6 tracking-tight">{c.value}</p>
              <p className="overline mt-2">{c.label}</p>
            </div>
          ))}
        </div>

        <div>
          <p className="overline mb-4">Invoices</p>
          {invoices.length === 0 && <Empty icon={FileText} title="No invoices yet" hint="Create an engaged-search invoice, or they'll appear after Stripe payments." />}
          {invoices.length > 0 && (
            <div className="border border-black/10">
              <div className="grid grid-cols-12 px-5 py-3 border-b border-black/10 overline text-[0.55rem] bg-white">
                <span className="col-span-3">Organization</span><span className="col-span-3">Type</span><span className="col-span-2">Amount</span><span className="col-span-2">Status</span><span className="col-span-2"></span>
              </div>
              {invoices.map((i) => (
                <div key={i.id} className="grid grid-cols-12 px-5 py-4 border-b border-black/5 bg-white items-center">
                  <span className="col-span-3 text-sm">{i.organization_name}</span>
                  <span className="col-span-3 text-sm capitalize flex items-center gap-2">{(i.invoice_type || "").replace(/^retained_search$/, "engaged_search").replace(/_/g, " ")}
                    {i.allow_installments && <span className="text-[0.5rem] font-mono text-primary border border-primary/30 px-1">{i.installments}× PLAN</span>}
                  </span>
                  <span className="col-span-2 font-heading font-bold">${i.amount}{i.amount_paid > 0 && i.status !== "paid" && <span className="block text-[0.6rem] font-sans font-normal text-muted-foreground">${i.amount_paid} paid</span>}</span>
                  <span className="col-span-2">
                    <Select value={i.status} onValueChange={(v) => changeStatus(i.id, v)}>
                      <SelectTrigger data-testid={`inv-status-${i.id}`} className="h-8 bg-white border-black/10 rounded-none text-xs capitalize">
                        <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full" style={{ background: INV_COLOR[i.status] || "#71717A" }} />{(i.status || "").replace(/_/g, " ")}</span>
                      </SelectTrigger>
                      <SelectContent className="bg-white border-black/10">
                        {INV_STATUSES.map((s) => <SelectItem key={s.v} value={s.v} data-testid={`inv-status-opt-${i.id}-${s.v}`}>{s.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </span>
                  <span className="col-span-2 text-right flex items-center justify-end gap-3">
                    {i.status !== "paid" && !i.allow_installments && <button onClick={() => toggleInstallments(i)} data-testid={`enable-inst-${i.id}`} className="text-xs text-muted-foreground hover:text-primary">Allow installments</button>}
                    <button onClick={() => removeInvoice(i)} data-testid={`delete-inv-${i.id}`} title="Delete invoice" className="p-1.5 border border-black/10 text-muted-foreground hover:text-[#EF4444] hover:border-[#EF4444]/40 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="overline mb-4">Subscriptions</p>
          {subs.length === 0 && <p className="text-sm text-muted-foreground">No active subscriptions.</p>}
          {subs.length > 0 && (
            <div className="space-y-px bg-black/10 border border-black/10">
              {subs.map((s) => (
                <div key={s.id} className="bg-white p-4 flex items-center justify-between">
                  <div><p className="font-medium">{s.organization_name}</p><p className="overline text-[0.55rem] mt-1 capitalize">{s.plan_name} · {s.status}</p></div>
                  <p className="font-heading font-bold">${s.amount}/mo</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
